import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';

import { VtmnPlayButtonShapeEnum } from '../buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { KeyboardEnum } from '~/component/enums/keyboard.enum';
import { slideInOut } from '~/core/helpers/animation/animation.helper';
import { generateUid } from '~/core/helpers/uid/uid.helper';
import { DOMService } from '~/core/services/dom/dom.service';

import { AbstractModal } from './classes/abstract-modal';
import { ModalVariantEnum } from './enums/modal-variant.enum';
import { CtaButtonTypeEnum } from '../call-to-action/enums/button-type.enum';
import { VtmnPlayIconButtonVariantEnum } from '../buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '../vtmn-play-icon/enums/vtmn-play-icon-type.enum';

/**
 * @see https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
 */
@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  animations: [slideInOut()],
})
export class ModalComponent extends AbstractModal implements OnDestroy {
  @Input() public readonly modalVariant: ModalVariantEnum =
    ModalVariantEnum.Primary;

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly modalId = generateUid();
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly domService: DOMService
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this.domService.enableBodyScroll();
  }

  public get cssClass(): string {
    if (this.modalVariant === ModalVariantEnum.Spaced) {
      return 'modal--spaced';
    }

    if (this.modalVariant === ModalVariantEnum.Secondary) {
      return 'modal--secondary';
    }

    if (this.modalVariant === ModalVariantEnum.Full) {
      return 'modal--full';
    }

    return null;
  }

  public get modalDescId(): string {
    return `${this.modalId}-description`;
  }

  public get modalTitleId(): string {
    return `${this.modalId}-title`;
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.autoClose) {
      return;
    }

    if (event.code === KeyboardEnum.Escape) {
      this.closeModal();
    }
  }

  public clickOutside(): void {
    if (!this.autoClose) {
      return;
    }

    this.closeModal();
  }

  public focusFirstFocusable(): void {
    this.elementRef.nativeElement.querySelector('button,a')?.focus();
  }

  public focusLastFocusable(): void {
    [...this.elementRef.nativeElement.querySelectorAll('button,a')]
      ?.pop()
      ?.focus();
  }

  public hide(): void {
    this.domService.enableBodyScroll();
    this.isVisible = false;
  }

  public onAnimationEvent(_event: AnimationEvent): void {
    if (this.isVisible) {
      this.focusFirstFocusable();
    }
  }

  public show(): void {
    this.isVisible = true;
    this.domService.disableBodyScroll();
  }
}
